import {
  faDownload,
  faSyncAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useLiveQuery } from "dexie-react-hooks";
import { saveAs } from "file-saver";
import moment from "moment";
import { useState } from "react";

import Confetti from "../../../assets/imgs/Confetti.png";
import PartyScribbles from "../../../assets/imgs/party-scribbles.png";
import { db } from "../../../db";
import { useSyncNotes } from "../../../hooks";
import { alert } from "../../common/Alert";
import {
  DSBadge,
  DSButton,
  DSCircularButton,
} from "../../design-system/molecules";
import { DSModal } from "../../design-system/organisms";
import NoteCreatedAt from "../../notebook/note/NoteCreatedAt";
import { ProgressBar } from "../inputs/AttachmentSelection";

function EmptyState() {
  return (
    <div className="mx-auto">
      <div className="flex flex-col items-center justify-center space-y-5">
        <div
          className="w-full min-h-[170px] flex-1 flex items-center justify-center"
          style={{
            backgroundImage: `url(${Confetti})`,
            backgroundRepeat: "repeat-x",
            backgroundSize: "contain",
          }}
        >
          <img
            className="flex self-center"
            width="100px"
            src={PartyScribbles}
          />
        </div>
        <h4 className="text-center text-base text-secondary">
          All recordings have been synced!
        </h4>
        <h5 className="max-w-2xl text-center text-sm text-tertiary">
          Yay! Your unsynced recordings have all been uploaded to our
          servers. You should now see them in your Inbox!
        </h5>
      </div>
    </div>
  );
}

function UnsyncedRecordingItem({
  item,
  blobURL,
  isSyncing,
  isQueued,
  percentCompleted,
  shouldShowSyncProgress,
  handleSync,
}) {
  const [
    shouldShowDeleteRecordingModal,
    setShouldShowDeleteRecordingModal,
  ] = useState(false);

  return (
    <>
      <DSModal
        isOpen={shouldShowDeleteRecordingModal}
        onClose={() => setShouldShowDeleteRecordingModal(false)}
        title="Delete this recording?"
        dataTestId="delete-recording-modal"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShouldShowDeleteRecordingModal(false),
            dataTestId: "delete-recording-cancel",
          },
          {
            label: "Yes, delete",
            variant: "primary",
            onClick: () => {
              db.unsyncedRecordings.delete(item.id);
              alert("success", "Recording deleted.");
              setShouldShowDeleteRecordingModal(false);
            },
            dataTestId: "delete-recording-confirm",
          },
        ]}
      >
        <p>Are you sure you&apos;d like to delete this recording?</p>
      </DSModal>

      <div className="p-sm bg-secondary border border-primary rounded-standard self-center flex flex-col transition-all space-y-3">
        <div className="flex-1 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <NoteCreatedAt createdAt={item.saved_date} />
            <DSBadge
              variant={item?.is_synced ? "green" : "red"}
              size="sm"
              dataTestId={`recording-status-${item.id}`}
            >
              {item?.is_synced ? "SYNCED" : "UNSYNCED"}
            </DSBadge>
          </div>
          <DSButton
            variant="secondary"
            size="sm"
            icon={faSyncAlt}
            onClick={() => handleSync(item)}
            disabled={isSyncing || isQueued}
            dataTestId={`sync-recording-${item.id}`}
          >
            {isSyncing
              ? "Syncing..."
              : isQueued
                ? "Queued for Sync"
                : "Sync"}
          </DSButton>
        </div>

        <audio controls className="flex-1 rounded-standard">
          <source src={blobURL} type="audio/wav" />
          Your browser does not support the audio element.
        </audio>

        <div className="flex-1 flex flex-row items-center space-x-2">
          <DSCircularButton
            size="sm"
            icon={faTimes}
            variant="danger"
            dataTestId={`delete-recording-${item.id}`}
            onClick={() => setShouldShowDeleteRecordingModal(true)}
          />

          <div className="flex-1">
            <ProgressBar
              width="w-full"
              uploadProgress={
                shouldShowSyncProgress ? percentCompleted : 0
              }
            />
          </div>

          <DSCircularButton
            size="sm"
            icon={faDownload}
            dataTestId={`download-recording-${item.id}`}
            onClick={() =>
              saveAs(
                item?.blob,
                `Scribenote-Recording-${moment(
                  item?.saved_date,
                ).format("MM-DD-YYYY-h-mm-a")}.wav`,
              )
            }
          />
        </div>
      </div>
    </>
  );
}

export default function UnsyncedRecordings({
  shouldShowUnsyncedRecordings,
  setShouldShowUnsyncedRecordings,
  createNewNote,
}) {
  const {
    addToSyncNotesQueue,
    uploadIndex,
    syncNotesQueue,
    uploadStatus,
  } = useSyncNotes();
  const percentCompleted = uploadStatus.numChunksTotal
    ? Math.round(
        (uploadStatus.numChunksCompleted * 100) /
          uploadStatus.numChunksTotal,
      )
    : 0;

  const allItems = useLiveQuery(
    () => db.unsyncedRecordings.toArray(),
    [],
  );

  const handleSync = (item) => {
    addToSyncNotesQueue(item, createNewNote);
  };

  const handleSyncAll = () => {
    allItems
      ?.filter(
        (item) =>
          !item.is_synced &&
          !syncNotesQueue.some(
            (queuedItem) => queuedItem.id === item.id,
          ),
      )
      .sort((a, b) => new Date(b.saved_date) - new Date(a.saved_date))
      .forEach((item) => {
        addToSyncNotesQueue(item, createNewNote);
      });
  };

  return (
    <DSModal
      isOpen={shouldShowUnsyncedRecordings}
      onClose={() => setShouldShowUnsyncedRecordings(false)}
      title="Unsynced Recordings"
      subtitle={
        allItems?.length > 0
          ? "Syncing recordings will upload them to our servers to be processed. A stable internet connection is required to sync recordings."
          : undefined
      }
      dataTestId="unsynced-recordings-modal"
      actions={[
        {
          label: "Close",
          variant: "cancel",
          onClick: () => setShouldShowUnsyncedRecordings(false),
          dataTestId: "unsynced-recordings-close",
        },
      ]}
    >
      {allItems?.length > 0 && (
        <div className="flex justify-end mb-sm">
          <DSButton
            variant="primary"
            size="sm"
            icon={faSyncAlt}
            onClick={handleSyncAll}
            dataTestId="sync-all-recordings"
          >
            Sync All
          </DSButton>
        </div>
      )}
      <div className="space-y-3">
        {allItems?.length > 0 ? (
          allItems
            .filter((item) => item?.blob)
            .sort(
              (a, b) =>
                new Date(b.saved_date) - new Date(a.saved_date),
            )
            .map((item) => {
              const blobURL = URL.createObjectURL(item.blob);
              const isSyncing = uploadIndex === item.id;
              const isQueued = syncNotesQueue.some(
                (queuedItem) => queuedItem.id === item.id,
              );

              return (
                <UnsyncedRecordingItem
                  key={item.id}
                  item={item}
                  blobURL={blobURL}
                  isSyncing={isSyncing}
                  isQueued={isQueued}
                  percentCompleted={percentCompleted}
                  shouldShowSyncProgress={isSyncing}
                  handleSync={handleSync}
                />
              );
            })
        ) : (
          <EmptyState />
        )}
      </div>
    </DSModal>
  );
}
